import React, { useState } from 'react';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { FormSection } from '@/shared/components/ui/FormSection';
import { FormField } from '@/shared/components/ui/FormField';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { AvatarUpload } from './AvatarUpload';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Icon } from '@/shared/icons';
import { useAccountSaveButton } from './account-save-context';

/**
 * Personal Information Section
 * Independent form for updating user profile data and avatar.
 * The primary "Salvar alterações" action is published to the page header
 * (single primary button); the form still submits on Enter.
 */
export const PersonalInfoSection: React.FC = () => {
  const { data: user, refetch } = useCurrentUser();
  const { updateUser } = useAuth();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    timezone: '',
    language: '',
    avatar: null as File | null
  });

  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleAvatarChange = (file: File) => {
    setFormData({ ...formData, avatar: file });
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleFieldChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSave = async (): Promise<void> => {
    setSuccessMessage('');
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      const payload = new FormData();

      // Add text fields (only if changed)
      if (formData.firstName) payload.append('firstName', formData.firstName);
      if (formData.lastName) payload.append('lastName', formData.lastName);
      if (formData.phone) payload.append('phone', formData.phone);
      if (formData.timezone) payload.append('timezone', formData.timezone);
      if (formData.language) payload.append('language', formData.language);

      // Add avatar file if selected
      if (formData.avatar) {
        payload.append('avatar', formData.avatar);
      }

      // Use api client (baseURL already includes /api)
      // Token automatically added by interceptor
      await api.put('/users/me/profile', payload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccessMessage('Informações atualizadas com sucesso!');

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        phone: '',
        timezone: '',
        language: '',
        avatar: null
      });
      setAvatarPreview('');

      // Refetch user data
      const { data: updatedUser } = await refetch();

      // Update AuthContext to reflect changes in header
      if (updatedUser) {
        updateUser(updatedUser);
      }

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.message || 'Erro ao atualizar informações. Tente novamente.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Publish the primary save action to the page header
  useAccountSaveButton({
    onSave: handleSave,
    saving: isSubmitting,
    label: 'Salvar alterações',
    loadingLabel: 'Salvando...',
  });

  if (!user) {
    return null;
  }

  return (
    <FormSection
      icon={'user'}
      title="Perfil"
      description="Suas informações pessoais e de contato"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSave();
        }}
        className="space-y-4"
        data-testid="account-personal-info-form"
      >
        {/* Success Message */}
        {successMessage && (
          <div
            className="flex items-center gap-2 p-3 rounded-lg"
            style={{
              backgroundColor: 'var(--theme-success-background)',
              borderLeft: '4px solid var(--theme-success-color)',
            }}
            data-testid="account-personal-info-success"
          >
            <Icon name="check-circle" className="w-5 h-5" style={{ color: 'var(--theme-success-color)' }} />
            <p className="text-sm" style={{ color: 'var(--theme-success-color)' }}>
              {successMessage}
            </p>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div
            className="flex items-center gap-2 p-3 rounded-lg"
            style={{
              backgroundColor: 'var(--theme-error-background)',
              borderLeft: '4px solid var(--theme-error-color)',
            }}
            data-testid="account-personal-info-error"
          >
            <Icon name="alert-circle" className="w-5 h-5" style={{ color: 'var(--theme-error-color)' }} />
            <p className="text-sm" style={{ color: 'var(--theme-error-color)' }}>
              {errorMessage}
            </p>
          </div>
        )}

        {/* Avatar Upload */}
        <FormField label="Foto de Perfil">
          <AvatarUpload
            currentAvatar={user.avatar}
            onAvatarChange={handleAvatarChange}
            preview={avatarPreview}
          />
        </FormField>

        {/* Nome e Sobrenome */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Nome" htmlFor="firstName">
            <Input
              id="firstName"
              type="text"
              value={formData.firstName || user.firstName}
              onChange={(e) => handleFieldChange('firstName', e.target.value)}
              placeholder="Seu nome"
              data-testid="account-first-name-input"
            />
          </FormField>

          <FormField label="Sobrenome" htmlFor="lastName">
            <Input
              id="lastName"
              type="text"
              value={formData.lastName || user.lastName}
              onChange={(e) => handleFieldChange('lastName', e.target.value)}
              placeholder="Seu sobrenome"
              data-testid="account-last-name-input"
            />
          </FormField>
        </div>

        {/* Telefone */}
        <FormField label="Telefone" htmlFor="phone" description="Formato: +55 11 99999-9999">
          <Input
            id="phone"
            type="tel"
            value={formData.phone || user.phone || ''}
            onChange={(e) => handleFieldChange('phone', e.target.value)}
            placeholder="+55 11 99999-9999"
            data-testid="account-phone-input"
          />
        </FormField>

        {/* Fuso Horário e Idioma */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Fuso Horário"
            value={formData.timezone || user.preferences?.timezone || 'America/Sao_Paulo'}
            onChange={(value) => handleFieldChange('timezone', value)}
            data-testid="account-timezone-select"
            options={[
              { value: 'America/Sao_Paulo', label: 'Brasília (GMT-3)' },
              { value: 'America/Manaus', label: 'Manaus (GMT-4)' },
              { value: 'America/Rio_Branco', label: 'Acre (GMT-5)' },
              { value: 'America/Noronha', label: 'Fernando de Noronha (GMT-2)' },
            ]}
          />

          <Select
            label="Idioma"
            value={formData.language || user.preferences?.language || 'pt-BR'}
            onChange={(value) => handleFieldChange('language', value)}
            data-testid="account-language-select"
            options={[
              { value: 'pt-BR', label: 'Português (Brasil)' },
              { value: 'en-US', label: 'English (US)' },
              { value: 'es-ES', label: 'Español' },
            ]}
          />
        </div>

        {/* Read-only Metadata */}
        <div className="pt-4 border-t" style={{ borderColor: 'var(--theme-card-border)' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Email
              </label>
              <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }} data-testid="account-email-readonly">
                {user.email}
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
                O email não pode ser alterado
              </p>
            </div>

            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Status
              </label>
              <div className="flex items-center gap-2">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{
                    backgroundColor:
                      user.status === 'active'
                        ? 'var(--theme-success-color)'
                        : 'var(--theme-error-color)',
                  }}
                />
                <span
                  className="text-sm capitalize"
                  style={{ color: 'var(--theme-card-text)' }}
                  data-testid="account-status-readonly"
                >
                  {user.status}
                </span>
              </div>
            </div>
          </div>
        </div>
      </form>
    </FormSection>
  );
};
